import { getPayoutChannelName } from "@/containers/app/payouts/mappers/banks.mapper";
import type { OrgBillRow } from "@/containers/app/org/hooks/useOrgBills";
import { statusMessagesMapping } from "@/constants/paymentStatusMessages";
import { formatDatetime, formatUtcDatetimeToColombian } from "../helpers/time";
import {
  Adjustment,
  Customer,
  Invoice,
  MerchantBill,
  Settlement,
  Subscription,
  SubscriptionGuest,
} from "../types/types";

// CUSTOMERS
export const CUSTOMER_TABLE_COLUMNS = [
  {
    key: "name",
    label: "Cliente",
    width: "3fr",
    minWidth: 250,
    exportTransform: (customer: Customer) => customer.name || "",
  },
  {
    key: "documentNumber",
    label: "Cédula",
    width: "1fr",
    minWidth: 150,
    exportTransform: (customer: Customer) => customer.documentNumber,
  },
  {
    key: "email",
    label: "Correo",
    width: "2fr",
    minWidth: 300,
    exportTransform: (customer: Customer) => customer.email || "",
  },
  {
    key: "phoneNumber",
    label: "Teléfono",
    width: "1fr",
    minWidth: 150,
    exportTransform: (customer: Customer) => customer.phoneNumber || "",
  },
  {
    key: "createdAt",
    label: "Fecha de creación",
    width: "1fr",
    minWidth: 200,
    exportTransform: (customer: Customer) =>
      formatUtcDatetimeToColombian(customer.createdAt),
  },
  {
    value: (customer: Customer) => (customer.muted ? "muted" : "unmuted"),
    key: "muted",
    label: "💬",
    width: "0.5fr",
    minWidth: 50,
    exportTransform: (customer: Customer) => (customer.muted ? "🔕" : "🔔"),
  },
  {
    value: (customer: Customer) => String(customer.riskScore ?? "N/A"),
    key: "riskScore",
    label: "Riesgo",
    width: "0.5fr",
    minWidth: 100,
    exportTransform: (customer: Customer) =>
      customer.riskScore
        ? {
            1: "Very High Risk",
            2: "High Risk",
            3: "Subprime",
            4: "Standard",
            5: "Prime",
          }[customer.riskScore] ?? "N/A"
        : "N/A",
  },
  {
    key: "edit",
    label: "",
    width: "0.5fr",
    minWidth: 50,
  },
] as const;

// SUBSCRIPTIONS
export const SUBSCRIPTION_TABLE_COLUMNS = [
  {
    key: "name",
    label: "Cliente",
    width: "3fr",
    minWidth: 250,
    exportTransform: (subscription: Subscription) => subscription.name || "",
  },
  {
    key: "documentNumber",
    label: "Cédula",
    width: "1fr",
    minWidth: 150,
    exportTransform: (subscription: Subscription) =>
      subscription.documentNumber,
  },
  {
    key: "email",
    label: "Correo",
    width: "2fr",
    minWidth: 300,
    exportTransform: (subscription: Subscription) => subscription.email || "",
  },
  {
    key: "phoneNumber",
    label: "Teléfono",
    width: "1fr",
    minWidth: 150,
    exportTransform: (subscription: Subscription) =>
      subscription.phoneNumber || "",
  },
  {
    key: "subscriptionSince",
    label: "Fecha de suscripción",
    width: "1fr",
    minWidth: 200,
    exportTransform: (subscription: Subscription) =>
      subscription.subscriptionSince
        ? formatUtcDatetimeToColombian(subscription.subscriptionSince)
        : "",
  },
] as const;

// SUBSCRIPTIONS - GUESTS
export const SUBSCRIPTIONS_GUEST_TABLE_COLUMNS = [
  {
    key: "name",
    label: "Invitado",
    width: "3fr",
    minWidth: 250,
    exportTransform: (guest: SubscriptionGuest) => guest.name || "",
  },
  {
    key: "documentNumber",
    label: "Cédula",
    width: "1fr",
    minWidth: 150,
    exportTransform: (guest: SubscriptionGuest) => guest.documentNumber,
  },
  {
    key: "email",
    label: "Correo",
    width: "2fr",
    minWidth: 300,
    exportTransform: (guest: SubscriptionGuest) => guest.email || "",
  },
  {
    key: "phoneNumber",
    label: "Teléfono",
    width: "1fr",
    minWidth: 150,
    exportTransform: (guest: SubscriptionGuest) => guest.phoneNumber || "",
  },
  {
    key: "subscriptionSince",
    label: "Fecha de suscripción",
    width: "1fr",
    minWidth: 200,
    exportTransform: (guest: SubscriptionGuest) =>
      guest.subscriptionSince ? formatDatetime(guest.subscriptionSince) : "",
  },
] as const;

// INVOICES
export const INVOICE_TABLE_COLUMNS = [
  {
    value: (invoice: Invoice) => invoice.propertyCode || invoice.contract,
    key: "propertyCode",
    label: "Contrato",
    width: "0.5fr",
    minWidth: 90,
    exportTransform: (invoice: Invoice) =>
      invoice.propertyCode || invoice.contract,
  },
  {
    value: (invoice: Invoice) => invoice.customerDocumentNumber,
    key: "customerDocumentNumber",
    label: "Cédula",
    width: "1fr",
    minWidth: 120,
    exportTransform: (invoice: Invoice) => invoice.customerDocumentNumber,
  },
  {
    value: (invoice: Invoice) => invoice.description,
    key: "description",
    label: "Descripción",
    width: "2fr",
    minWidth: 230,
    exportTransform: (invoice: Invoice) => invoice.description,
  },
  {
    value: (invoice: Invoice) => invoice.dueDate,
    key: "dueDate",
    label: "Fecha corte",
    width: "1fr",
    minWidth: 120,
    exportTransform: (invoice: Invoice) => invoice.dueDate,
  },
  {
    value: (invoice: Invoice) => invoice.paymentAmount ?? invoice.amount,
    key: "amount",
    label: "Valor",
    width: "1fr",
    minWidth: 120,
    exportTransform: (invoice: Invoice) =>
      invoice.paymentAmount ?? invoice.amount,
  },
  {
    value: (invoice: Invoice) => invoice.id,
    key: "id",
    label: "ID",
    width: "1fr",
    minWidth: 130,
    exportTransform: (invoice: Invoice) => invoice.id,
  },
  {
    value: (invoice: Invoice) =>
      (invoice.status === "ready" || invoice.status === "cancelled") &&
      invoice.paymentDate
        ? "paymentProcessing"
        : invoice.status === "cancelled" && invoice.allowsCancelledPayment
        ? "cancelledPaymentEnabled"
        : invoice.status === "paid" && invoice.partialPayment
        ? "partiallyPaid"
        : invoice.status,
    key: "status",
    label: "Estado",
    width: "1fr",
    minWidth: 135,
    exportTransform: (invoice: Invoice) =>
      (invoice.status === "ready" || invoice.status === "cancelled") &&
      invoice.paymentDate
        ? "Pago en Curso"
        : invoice.status === "cancelled" && invoice.allowsCancelledPayment
        ? "Pago Habilitado"
        : invoice.status === "paid" && invoice.partialPayment
        ? "Pago Parcial"
        : {
            pending: "Sin Pagar",
            ready: "Sin Pagar",
            paid: "Pagado",
            cancelled: "Archivado",
            chargeback: "Reversado",
          }[invoice.status],
  },
  {
    value: (invoice: Invoice) => invoice.creationMethod,
    key: "creationMethod",
    label: "Creación",
    width: "1fr",
    minWidth: 100,
    exportTransform: (invoice: Invoice) =>
      ({ manual: "Manual", bulk: "Archivo", erp: "ERP", api: "API" }[
        invoice.creationMethod
      ]),
  },
  {
    value: (invoice: Invoice) => invoice.erpSyncStatus,
    key: "erpSyncStatus",
    label: "ERP",
    width: "0.5fr",
    minWidth: 50,
    exportTransform: (invoice: Invoice) =>
      ({
        success: "Automático",
        retry: "Reintento Exitoso",
        manual: "Manual",
        off_hours: "Pendiente",
        processing: "En Proceso",
        error: "Con Error",
        none: "Sin Contabilizar",
      }[invoice.erpSyncStatus]),
  },
  {
    value: (invoice: Invoice) => invoice.sent,
    key: "sent",
    label: "Envío",
    width: "0.5fr",
    minWidth: 55,
    exportTransform: (invoice: Invoice) => (invoice.sent ? "No" : "Si"),
  },
  {
    value: () => undefined,
    key: "actions",
    label: "",
    width: "0.5fr",
    minWidth: 50,
  },
] as const;

// MERCHANT BILLS
export const MERCHANT_BILL_TABLE_COLUMNS = [
  {
    key: "period",
    label: "Periodo",
    width: "1fr",
    minWidth: 120,
    exportTransform: (bill: MerchantBill) => bill.period,
  },
  {
    key: "dueDate",
    label: "Fecha límite",
    width: "1fr",
    minWidth: 130,
    exportTransform: (bill: MerchantBill) => bill.dueDate,
  },
  {
    key: "type",
    label: "Tipo",
    width: "1fr",
    minWidth: 120,
    exportTransform: (bill: MerchantBill) =>
      bill.type === "prepaid" ? "Descontada" : "Mes Vencido",
  },
  {
    key: "amount",
    label: "Valor",
    width: "1fr",
    minWidth: 120,
    exportTransform: (bill: MerchantBill) => bill.amount,
  },
  {
    key: "paid",
    label: "Estado",
    width: "1fr",
    minWidth: 120,
    exportTransform: (bill: MerchantBill) =>
      bill.paid ? "Pagado" : "Sin Pagar",
  },
  {
    key: "actions",
    label: "",
    width: "0.8fr",
    minWidth: 120,
  },
] as const;

// ORG BILLS
export const ORG_BILL_TABLE_COLUMNS = [
  {
    key: "merchant",
    label: "Comercio",
    width: "1.5fr",
    minWidth: 160,
    exportTransform: (bill: OrgBillRow) => bill.merchant,
  },
  {
    key: "period",
    label: "Periodo",
    width: "1fr",
    minWidth: 120,
    exportTransform: (bill: OrgBillRow) => bill.period,
  },
  {
    key: "dueDate",
    label: "Fecha límite",
    width: "1fr",
    minWidth: 130,
    exportTransform: (bill: OrgBillRow) => bill.dueDate,
  },
  {
    key: "type",
    label: "Tipo",
    width: "1fr",
    minWidth: 120,
    exportTransform: (bill: OrgBillRow) =>
      bill.type === "prepaid" ? "Descontada" : "Mes Vencido",
  },
  {
    key: "amount",
    label: "Valor",
    width: "1fr",
    minWidth: 120,
    exportTransform: (bill: OrgBillRow) => bill.amount,
  },
  {
    key: "paid",
    label: "Estado",
    width: "1fr",
    minWidth: 120,
    exportTransform: (bill: OrgBillRow) => (bill.paid ? "Pagado" : "Sin Pagar"),
  },
  {
    key: "actions",
    label: "",
    width: "0.8fr",
    minWidth: 120,
  },
] as const;

// SETTLEMENTS
export const SETTLEMENT_TABLE_COLUMNS = [
  {
    key: "date",
    label: "Fecha abono",
    width: "1.5fr",
    exportTransform: (settlement: Settlement) => settlement.date,
  },
  {
    key: "cycle",
    label: "Hora",
    width: "1fr",
    exportTransform: (settlement: Settlement) => settlement.cycle,
  },
  {
    key: "numberOfInvoices",
    label: "Recaudos",
    width: "1fr",
    exportTransform: (settlement: Settlement) => settlement.numberOfInvoices,
  },
  {
    key: "amountCollected",
    label: "Valor recaudado",
    width: "1fr",
    format: "currency",
    exportTransform: (settlement: Settlement) => settlement.amountCollected,
  },
  {
    key: "fees",
    label: "Comisión",
    width: "0.8fr",
    format: "currency",
    exportTransform: (settlement: Settlement) => settlement.fees,
  },
  {
    key: "feesPostpaid",
    label: "Tipo Comisión",
    width: "0.8fr",
    minWidth: 120,
    format: "badge",
    exportTransform: (settlement: Settlement) =>
      settlement.feesPostpaid ? "Mes Vencido" : "Descontada",
  },
  {
    key: "adjustments",
    label: "Valor ajustes",
    width: "1fr",
    format: "currency",
    exportTransform: (settlement: Settlement) => settlement.adjustments,
  },
  {
    key: "paymentAmount",
    label: "Valor abono",
    width: "1fr",
    format: "currency",
    exportTransform: (settlement: Settlement) => settlement.paymentAmount,
  },
  {
    key: "status",
    label: "Estado",
    width: "1fr",
    minWidth: 110,
    format: "badge",
    tooltip: (settlement: Settlement) => {
      if (settlement.status !== "error" || !settlement.statusMessage) return;
      return statusMessagesMapping[settlement.statusMessage];
    },
    exportTransform: (settlement: Settlement) =>
      ({
        processing: "Procesando",
        paid: "Pagado",
        credited: "Acreditado",
        error: "Error",
      }[settlement.status]),
  },
] as const;

export const SETTLEMENT_INVOICES_TABLE_COLUMNS = [
  {
    key: "customerDocumentNumber",
    label: "Cédula",
    width: "1fr",
    minWidth: 120,
    exportTransform: (invoice: Invoice) => invoice.customerDocumentNumber,
  },
  {
    key: "description",
    label: "Descripción",
    width: "2fr",
    minWidth: 230,
    exportTransform: (invoice: Invoice) => invoice.description,
  },
  {
    key: "payedAt",
    label: "Fecha de pago",
    width: "1fr",
    minWidth: 200,
    format: "datetime",
    exportTransform: (invoice: Invoice) => formatDatetime(invoice.payedAt!),
  },
  {
    key: "paymentAmount",
    label: "Recaudo",
    width: "1fr",
    minWidth: 120,
    format: "currency",
    exportTransform: (invoice: Invoice) => invoice.paymentAmount!,
  },
  {
    key: "paymentFee",
    label: "Comisión",
    width: "1fr",
    minWidth: 120,
    format: "currency",
    exportTransform: (invoice: Invoice) => invoice.paymentFee!,
  },
  {
    key: "id",
    label: "ID",
    width: "1fr",
    minWidth: 130,
    exportTransform: (invoice: Invoice) => invoice.id,
  },
  {
    key: "paymentMethod",
    label: "Riel de Pago",
    width: "1fr",
    minWidth: 120,
    format: "badge",
    exportTransform: (invoice: Invoice) =>
      ({
        pse: "PSE",
        nequiButton: "Nequi",
        bancolombiaButton: "Bancolombia",
        card: "Tarjeta",
        breb: "Bre-B",
        cash: "Efectivo",
      }[invoice.paymentMethod!]),
  },
  {
    key: "creationMethod",
    label: "Creación",
    width: "1fr",
    minWidth: 100,
    format: "badge",
    exportTransform: (invoice: Invoice) =>
      ({ manual: "Manual", bulk: "Archivo", erp: "ERP", api: "API" }[
        invoice.creationMethod
      ]),
  },
  {
    key: "erpSyncStatus",
    label: "ERP",
    width: "0.5fr",
    minWidth: 50,
    format: "status",
    exportTransform: (invoice: Invoice) =>
      ({
        success: "Automático",
        retry: "Reintento Exitoso",
        manual: "Manual",
        off_hours: "Pendiente",
        processing: "En Proceso",
        error: "Con Error",
        none: "Sin Contabilizar",
      }[invoice.erpSyncStatus]),
  },
] as const;

export const SETTLEMENT_ADJUSTMENTS_TABLE_COLUMNS = [
  {
    key: "createdAt",
    label: "Fecha de creación",
    width: "1.25fr",
    minWidth: 200,
    format: "datetime",
    exportTransform: (adjustment: Adjustment) =>
      formatUtcDatetimeToColombian(adjustment.createdAt),
  },
  {
    key: "amount",
    label: "Valor",
    width: "1fr",
    minWidth: 150,
    format: "currency",
    exportTransform: (adjustment: Adjustment) => adjustment.amount,
  },
  {
    key: "description",
    label: "Descripción",
    width: "6fr",
    minWidth: 300,
    exportTransform: (adjustment: Adjustment) => adjustment.description,
  },
] as const;

// PAYOUTS - LEDGER
export const LEDGER_TABLE_COLUMNS = [
  {
    key: "sourceType",
    label: "Tipo",
    width: "1fr",
    minWidth: 120,
    exportTransform: (entry: { sourceType: string }) => {
      const labels: Record<string, string> = {
        settlement: "Liquidación",
        payoutBatch: "Dispersión",
        payoutRefund: "Reembolso dispersión",
        withdrawal: "Retiro",
        withdrawalRefund: "Reembolso retiro",
        topUp: "Carga de fondos",
      };
      return labels[entry.sourceType] || entry.sourceType;
    },
  },
  {
    key: "createdAt",
    label: "Fecha",
    width: "1.5fr",
    minWidth: 180,
    exportTransform: (entry: { createdAt: string }) => entry.createdAt,
  },
  {
    key: "sourceId",
    label: "Referencia",
    width: "1.5fr",
    minWidth: 150,
    exportTransform: (entry: { sourceId: string }) => entry.sourceId,
  },
  {
    key: "amount",
    label: "Monto",
    width: "1.5fr",
    minWidth: 150,
    format: "currency",
    exportTransform: (entry: { amount: number }) => entry.amount,
  },
  {
    key: "resultingBalance",
    label: "Saldo",
    width: "1.5fr",
    minWidth: 150,
    format: "currency",
    exportTransform: (entry: { resultingBalance: number }) =>
      entry.resultingBalance,
  },
] as const;

// PAYOUTS - BATCHES
export const PAYOUT_BATCHES_TABLE_COLUMNS = [
  {
    key: "id",
    label: "Lote ID",
    width: "1fr",
    minWidth: 120,
    exportTransform: (batch: { id: string }) => batch.id,
  },
  {
    key: "type",
    label: "Tipo",
    width: "1fr",
    minWidth: 140,
    exportTransform: (batch: { type?: string }) =>
      batch.type === "buildingManagement" ? "Administraciones" : "Propietarios",
  },
  {
    key: "createdAt",
    label: "Fecha de Creación",
    width: "1.5fr",
    minWidth: 180,
    exportTransform: (batch: { createdAt: string }) => batch.createdAt,
  },
  {
    key: "status",
    label: "Estado",
    width: "1fr",
    minWidth: 120,
    format: "badge",
    exportTransform: (batch: { status: string }) => {
      const labels: Record<string, string> = {
        preparing: "Preparando",
        pending: "Pendiente",
        unfunded: "Sin fondos",
        funding: "Fondos en camino",
        approved: "Aprobado",
        cancelled: "Cancelado",
        failed: "Fallido",
      };
      return labels[batch.status] ?? batch.status;
    },
  },
  {
    key: "totalCount",
    label: "Cant. Pagos",
    width: "1fr",
    minWidth: 120,
    exportTransform: (batch: { totalCount: number }) => batch.totalCount,
  },
  {
    key: "totalAmount",
    label: "Monto Total",
    width: "1.5fr",
    minWidth: 150,
    format: "currency",
    exportTransform: (batch: { totalAmount: number }) => batch.totalAmount,
  },
  {
    key: "totalFees",
    label: "Comisión",
    width: "1.5fr",
    minWidth: 150,
    format: "currency",
    exportTransform: (batch: { totalFees: number }) => batch.totalFees,
  },
  {
    key: "gmfTotal",
    label: "GMF",
    width: "1.5fr",
    minWidth: 150,
    format: "currency",
    exportTransform: (batch: { gmfTotal?: number }) => batch.gmfTotal ?? 0,
  },
] as const;

// PAYOUTS - WITHDRAWALS
export const WITHDRAWALS_TABLE_COLUMNS = [
  {
    key: "createdAt",
    label: "Fecha",
    width: "1.5fr",
    minWidth: 180,
    exportTransform: (withdrawal: { createdAt: string }) =>
      withdrawal.createdAt,
  },
  {
    key: "amount",
    label: "Monto",
    width: "1.5fr",
    minWidth: 150,
    format: "currency",
    exportTransform: (withdrawal: { amount: number }) => withdrawal.amount,
  },
  {
    key: "totalFee",
    label: "Comisión",
    width: "1.5fr",
    minWidth: 150,
    format: "currency",
    exportTransform: (withdrawal: { totalFee: number }) => withdrawal.totalFee,
  },
  {
    key: "status",
    label: "Estado",
    width: "1fr",
    minWidth: 120,
    format: "badge",
    exportTransform: (withdrawal: { status: string }) =>
      withdrawal.status === "completed"
        ? "Completado"
        : withdrawal.status === "processing"
        ? "En proceso"
        : withdrawal.status === "failed"
        ? "Fallido"
        : "Pendiente",
  },
  {
    key: "account",
    label: "Cuenta Destino",
    width: "3fr",
    minWidth: 250,
    exportTransform: (withdrawal: { account: string }) => withdrawal.account,
  },
] as const;

// PAYOUTS - ALL PAYOUTS (DETALLE DE DISPERSIONES)
export const ALL_PAYOUTS_TABLE_COLUMNS = [
  {
    key: "createdAt",
    label: "Fecha",
    width: "1.5fr",
    minWidth: 150,
    exportTransform: (payout: { createdAt: string }) => payout.createdAt,
  },
  {
    key: "batchId",
    label: "Lote",
    width: "1.5fr",
    minWidth: 150,
    exportTransform: (payout: { batchId: string }) => payout.batchId,
  },
  {
    key: "customerName",
    label: "Nombre",
    width: "2fr",
    minWidth: 180,
    exportTransform: (payout: { customerName: string }) => payout.customerName,
  },
  {
    key: "customerDocumentNumber",
    label: "Documento",
    width: "1.5fr",
    minWidth: 130,
    exportTransform: (payout: { customerDocumentNumber: string }) =>
      payout.customerDocumentNumber,
  },
  {
    key: "amount",
    label: "Monto",
    width: "1.5fr",
    minWidth: 150,
    format: "currency",
    exportTransform: (payout: { amount: number }) => payout.amount,
  },
  {
    key: "bank",
    label: "Banco",
    width: "1.5fr",
    minWidth: 150,
    exportTransform: (payout: {
      bank?: string;
      manualPaymentMethod?: string;
    }) => getPayoutChannelName(payout),
  },
  {
    key: "status",
    label: "Estado",
    width: "1fr",
    minWidth: 120,
    format: "badge",
    exportTransform: (payout: { status: string }) =>
      payout.status === "completed"
        ? "Completado"
        : payout.status === "processing"
        ? "Procesando"
        : payout.status === "cancelled"
        ? "Cancelado"
        : payout.status === "declined"
        ? "Rechazado"
        : "Pendiente",
  },
  {
    key: "sent",
    label: "Envío",
    width: "0.5fr",
    minWidth: 55,
    exportTransform: (payout: { ownerNotifiedAt?: string }) =>
      payout.ownerNotifiedAt ? "Si" : "No",
  },
] as const;
